import { useEffect, useMemo } from "react";
import {
  bayHireRequestFormDefaultValues,
  createBayHireRequestFormSchema,
  BAY_HIRE_REQUEST_FORM_FIELDS as F,
  type BayHireRequestFormData,
} from "@features/bay-hire/schema/bayHireRequestFormSchema";
import { useBayHireRequestFormStore } from "@features/bay-hire/store/useBayHireRequestForm.store";
import { usePersistentForm } from "@hooks/usePersistentForm";
import type { CompanyDetails } from "@services/bayHireApi";
import { convertUaeToUtc } from "@utils/date-time";
import type { useForm } from "react-hook-form";

/**
 * DFT-0044 slice T: the hire period is a date-TIME contract-wise (OffsetDateTime), so a full
 * instant is still submitted here even though the picker itself is date-only (Megaplan item 3,
 * 2026-08-05 - see below).
 *
 * 🔴 This replaced a `toISODate` that formatted `YYYY-MM-DD` from the local date parts, throwing the
 * time away. Once the domain moved these fields to OffsetDateTime, a date-only string could not be
 * deserialized at all and every create failed with 400 "Request body is missing or malformed" - the
 * body was well-formed JSON, so the message pointed nowhere near the cause.
 *
 * `toISOString()` is the platform-wide idiom (D47) and emits UTC, which is what the contract wants.
 *
 * Megaplan (2026-08-05, item 3): Date From/Date To no longer show a time picker (`timePicker=false`
 * on `BayHireRequestForm.tsx`'s two date fields) - a vendor never chooses a time for these anymore.
 * Normalizes to start-of-day before converting, so the submitted instant is always midnight
 * regardless of what moment the picker happened to be interacted with (there is no time UI left for
 * that moment to matter) - NOT dropping the time component from the wire contract entirely, which
 * is what caused the original 400 this comment's own history records.
 *
 * **DFT-0109 fix**: "start-of-day" now means Asia/Dubai midnight, not the picking browser's own
 * local midnight - the previous implementation called `d.setHours(0,0,0,0)` (browser-local) then
 * `.toISOString()`, so a vendor whose browser was not set to Asia/Dubai would silently submit an
 * instant shifted by their own UTC offset from the calendar day they actually picked. `setHours`
 * only rewrites the time-of-day fields, not year/month/day, so the picked calendar day is still
 * safe to feed into `convertUaeToUtc` (the shared, already-Asia/Dubai-explicit conversion also
 * used by `vessel-nomination`/`deficiencies`/`terminal-services`, see `src/utils/date-time.tsx`)
 * regardless of the browser's own timezone.
 *
 * NOT used for `receiptDate`, which is deliberately still a date-only `LocalDate` in the domain: a
 * receipt is dated, not timed. `ReceiptUploadDialog` keeps its own date-only formatting for that
 * reason - do not "tidy" the two together.
 */
const toISOInstant = (d: Date) => {
  const startOfDay = new Date(d);
  startOfDay.setHours(0, 0, 0, 0);
  return convertUaeToUtc(startOfDay);
};

interface UseBayHireRequestFormProps {
  initialValues?: Partial<BayHireRequestFormData>;
  disabled?: boolean;
  /**
   * Prefill support (2026-08-05): "Name of Port company responsible" / "Designation in the
   * company" default from the caller's own APPROVED Port Operator License
   * (`useBayHireMyCompanyDetails`'s `responsiblePersonName`/`responsiblePersonDesignation`,
   * license-service via the BFF - see CompanyDetails' own doc). Resolves asynchronously, after
   * this form's `defaultValues` are already fixed at mount, so it is seeded via an effect below
   * rather than `defaultValues` itself.
   */
  companyDetails?: CompanyDetails | null;
}

interface UseBayHireRequestFormResult {
  form: ReturnType<typeof useForm<BayHireRequestFormData>>;
  isFormDisabled: boolean;
  handleDateFieldChange: (key: typeof F.DATE_FROM | typeof F.DATE_TO, value: unknown) => void;
}

export const useBayHireRequestForm = (
  props: UseBayHireRequestFormProps,
): UseBayHireRequestFormResult => {
  const { initialValues, disabled, companyDetails } = props;
  // DFT-0050 (Update Request bug fix): Update/Resubmit/Renew prefill dateFrom/dateTo straight
  // from an existing request, which is very often already in the past by the time it is edited -
  // see createBayHireRequestFormSchema's own doc for why an unconditional "not in the past" check
  // silently blocked submission (no backend call, no visible error) whenever that happened. Only
  // built once per mount (initialValues never changes identity after the initial render on any of
  // these forms), so this does not churn the resolver on every keystroke.
  const schema = useMemo(
    () =>
      createBayHireRequestFormSchema({
        originalDateFrom: initialValues?.dateFrom,
        originalDateTo: initialValues?.dateTo,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // DFT-0096: field values are backed by useBayHireRequestFormStore (a Zustand
  // `persist` store, localStorage-backed) via the shared `usePersistentForm`
  // hook, so an accidental refresh/navigation-away doesn't silently discard
  // what the vendor already typed. `setAll`/`resetForm` mirror
  // `useVesselRegisterFormStore`'s own precedent exactly. No `dateKeys` -
  // unlike vessel-register's schema, `dateFrom`/`dateTo` stay plain ISO
  // strings throughout this form (see `toISOInstant` above), never `Date`
  // objects, so there is nothing for usePersistentForm to normalize.
  //
  // `storeValues` is deliberately NOT the raw store snapshot: `mergeDefaults`
  // (inside usePersistentForm) always lets any key present in `storeValues`
  // win over `defaultValues`, unconditionally. If the store is still blank
  // (never touched by typing - e.g. a request that was just opened, or one
  // whose draft has since been cleared), passing the raw snapshot would let
  // its blank `""`/`[]` values silently clobber the real prefill Resubmit/
  // Update/Renew just computed from the fetched request into `defaultValues`.
  // Passing `{}` instead when the store is blank makes `mergeDefaults` fall
  // straight through to `defaultValues` unchanged, which is exactly right -
  // nothing has been typed yet, so the server's current values should show.
  // Once the vendor actually edits something, the watch-based sync effect
  // below populates the store for real, and from then on its values
  // correctly take priority (a persisted draft is meant to override a fresh
  // prefill on a later mount/refresh).
  const storeSnapshot = useBayHireRequestFormStore.getState();
  const isStoreBlank = !storeSnapshot.bayPreference && !storeSnapshot.dateFrom;
  const setAll = useBayHireRequestFormStore((state) => state.setAll);
  const resetForm = useBayHireRequestFormStore((state) => state.resetForm);

  const form = usePersistentForm<BayHireRequestFormData>({
    schema,
    defaultValues: { ...bayHireRequestFormDefaultValues, ...initialValues },
    storeValues: isStoreBlank ? {} : storeSnapshot,
    setStoreValues: setAll,
    resetStore: resetForm,
    persistKey: "bay-hire-request-form",
  });

  const { setValue, getValues, formState, watch } = form;
  const isFormDisabled = disabled || formState.isSubmitting;

  // Sync every field change into the persisted store as the vendor types, so
  // a refresh restores in-progress input (not just whatever was on the form
  // at last mount).
  useEffect(() => {
    const subscription = watch((values) => setAll(values as Partial<BayHireRequestFormData>));
    return () => subscription.unsubscribe();
  }, [watch, setAll]);

  // Prefill support: seeds a STARTING VALUE only, never overwrites one that is already present -
  // whether from `initialValues` (Update/Resubmit/Renew's own previously-submitted value, which
  // must always win over a license-derived default) or from the vendor having already typed
  // something into the field themselves before this query resolved. Guarded per-field, since a
  // license record commonly has one populated field and not the other.
  useEffect(() => {
    if (!companyDetails) return;
    if (companyDetails.responsiblePersonName && !getValues(F.RESPONSIBLE_PERSON_NAME)) {
      setValue(F.RESPONSIBLE_PERSON_NAME, companyDetails.responsiblePersonName);
    }
    if (
      companyDetails.responsiblePersonDesignation &&
      !getValues(F.RESPONSIBLE_PERSON_DESIGNATION)
    ) {
      setValue(F.RESPONSIBLE_PERSON_DESIGNATION, companyDetails.responsiblePersonDesignation);
    }
  }, [companyDetails, getValues, setValue]);

  const handleDateFieldChange = (key: typeof F.DATE_FROM | typeof F.DATE_TO, value: unknown) => {
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      setValue(key, toISOInstant(value), { shouldValidate: true, shouldDirty: true });
    } else {
      setValue(key, "", { shouldValidate: true, shouldDirty: true });
    }
  };

  return { form, isFormDisabled, handleDateFieldChange };
};
